/**
 * denk-collect cart attribution + commerce events (BLACKOUTSOLAR theme).
 *
 * Loaded site-wide from layout/theme.liquid. Built around the self-hosted
 * denk-collect SDK (window.denk, created by snippets/denk-collect-loader.liquid,
 * which sets window.__denkReady once denk.init() has run).
 *
 *   1. Consent-tiered attribution written into Shopify cart attributes. Standard
 *      checkout turns cart attributes into order note_attributes, which the
 *      denk-collect orders/create webhook reads to (a) stitch the order to its
 *      full first-party session journey and (b) recover the ad click id for
 *      server-side Google Ads attribution. TWO consent lanes (see the matrix in
 *      writeAttributionToCart):
 *        • ANALYTICS *or* MARKETING → the first-party ids (_denk_id / _denk_session)
 *          + the session's landing path + referrer + the analytics-consent flag
 *          + the current inquiry id (_denk_lead, from denk.lead.current(), null
 *          without consent; re-written on every `denk:lead` event).
 *          These are FIRST-PARTY ANALYTICS identifiers (order↔session join keys),
 *          governed by analytics consent — they never leave the house.
 *        • MARKETING (adds to the above) → the Google/Meta consent flags the
 *          webhook reads (_denk_consent_ad_user_data / _denk_consent_ad_personalization)
 *          and the ad click ids (gclid/gbraid/wbraid + Meta fbclid/fbc/fbp).
 *        • NEITHER → nothing is written; anything previously written is nulled.
 *      Ids come from window.denk.getIds() (null pre-consent), so this asset never
 *      reads the SDK's identity cookies itself. Google click ids captured from the
 *      URL are kept IN MEMORY until marketing consent exists — persisting them to
 *      localStorage pre-consent would itself be a regulated storage act
 *      (TTDSG §25 / Austrian TKG §165).
 *
 *   2. add_to_cart -> denk.track, keyed off the theme's `variant:add` event
 *      (assets/theme.js.liquid) which carries the exact added line items. A cart
 *      change is ALSO the moment a fresh cart is created, so we re-sync the
 *      attribution then (a checkout must carry ids set BEFORE checkout).
 *
 *   3. begin_checkout -> denk.track, once per page, across all cart-page checkout
 *      paths: the normal `name="checkout"` submit AND the VAT-exemption
 *      (mwst-befreiung) clone, whose exempt branch redirects without submitting.
 *
 * denk.track buffers in-memory until ANALYTICS consent and only sends afterwards, so
 * the commerce events are emitted unconditionally and the SDK gates them. The cart
 * attribute writes are the pieces we gate on consent ourselves.
 */
(function () {
  'use strict';

  // Google ad click ids captured from the URL (marketing lane).
  var CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'];
  var STORE_KEY = 'denk_click_ids';
  var WRITTEN_KEY = 'denk_cart_attr_written';
  var STASH_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days — Google click-id validity

  // Every cart attribute this asset may write — the exhaustive null-set used on a
  // marketing/analytics withdrawal, so nothing we ever wrote rides into an order.
  var ALL_KEYS = [
    '_denk_id',
    '_denk_session',
    '_denk_landing_path',
    '_denk_referrer',
    '_denk_consent_analytics',
    '_denk_lead',
    '_denk_consent_ad_user_data',
    '_denk_consent_ad_personalization',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'fbc',
    'fbp'
  ];

  // The subset written only under a MARKETING grant.
  var MARKETING_KEYS = [
    '_denk_consent_ad_user_data',
    '_denk_consent_ad_personalization',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'fbc',
    'fbp'
  ];

  // Click ids captured from THIS page's URL, kept in memory only. They are NOT
  // persisted to localStorage until marketing consent exists (see the stash).
  var pending = {};

  function captureClickIds() {
    try {
      var params = new URLSearchParams(window.location.search);
      CLICK_PARAMS.forEach(function (k) {
        var v = params.get(k);
        if (v) pending[k] = v;
      });
      var fbclid = params.get('fbclid');
      if (fbclid) pending.fbclid = fbclid;
    } catch (e) {}
  }

  // localStorage stash is read/written ONLY under marketing consent.
  function readStash() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (!raw || !raw.ids) return {};
      if (raw.ts && Date.now() - raw.ts > STASH_TTL) {
        localStorage.removeItem(STORE_KEY);
        return {};
      }
      return raw.ids;
    } catch (e) {
      return {};
    }
  }
  function writeStash(ids) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ ids: ids, ts: Date.now() }));
    } catch (e) {}
  }
  function clearStash() {
    try {
      localStorage.removeItem(STORE_KEY);
    } catch (e) {}
  }

  function readConsent() {
    try {
      var rec = window.denk && window.denk.getConsent && window.denk.getConsent();
      return (rec && rec.choices) || null;
    } catch (e) {
      return null;
    }
  }
  function hasMarketing(choices) {
    var c = choices || readConsent();
    return !!(c && c.marketing);
  }
  function hasAnalytics(choices) {
    var c = choices || readConsent();
    // The first-party analytics lane opens on EITHER toggle (marketing implies the
    // measurement grant); the SDK mints identity on either grant too.
    return !!(c && (c.analytics || c.marketing));
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // First-party ids from the SDK (null unless analytics/marketing granted). Never
  // reads _denk_id from document.cookie directly — the SDK owns that.
  function readDenkIds() {
    try {
      return (window.denk && window.denk.getIds && window.denk.getIds()) || null;
    } catch (e) {
      return null;
    }
  }

  // Current inquiry {leadId, ref} from the SDK (null pre-consent or without the
  // lead API in an older SDK).
  function readCurrentLead() {
    try {
      var api = window.denk && window.denk.lead;
      return (api && typeof api.current === 'function' && api.current()) || null;
    } catch (e) {
      return null;
    }
  }

  // Read a raw cookie value (used only for the Meta _fbp/_fbc first-party cookies
  // under marketing consent — not for the SDK's own identity).
  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  }

  // Assemble the Google click ids for the marketing lane, persisting freshly seen
  // URL ids to the stash now that consent exists.
  function marketingClickIds() {
    var ids = readStash();
    var changed = false;
    CLICK_PARAMS.forEach(function (k) {
      if (pending[k] && ids[k] !== pending[k]) {
        ids[k] = pending[k];
        changed = true;
      }
    });
    if (pending.fbclid && ids.fbclid !== pending.fbclid) {
      ids.fbclid = pending.fbclid;
      changed = true;
    }
    if (changed) writeStash(ids); // persist only now that consent exists
    // Meta: prefer an existing _fbc cookie, else derive one from a captured fbclid;
    // include _fbp when present. These are Meta's own first-party cookies.
    var fbc = readCookie('_fbc');
    if (!fbc && (ids.fbclid || pending.fbclid)) {
      fbc = 'fb.1.' + Date.now() + '.' + (ids.fbclid || pending.fbclid);
    }
    if (fbc) ids.fbc = fbc;
    var fbp = readCookie('_fbp');
    if (fbp) ids.fbp = fbp;
    return ids;
  }

  // Consent-tiered cart attribute writer. Composes the full desired attribute set
  // for the CURRENT decision and writes it (or clears everything on withdrawal),
  // once per session unless the set actually changed.
  //
  //   consent lane    | attributes written
  //   ----------------+-------------------------------------------------------------
  //   analytics|mkt   | _denk_id, _denk_session, _denk_landing_path, _denk_referrer,
  //                   |   _denk_consent_analytics=granted, _denk_lead (if any)
  //   marketing (adds)| _denk_consent_ad_user_data=granted,
  //                   |   _denk_consent_ad_personalization=granted,
  //                   |   gclid/gbraid/wbraid, fbclid/fbc/fbp
  //   neither         | (all keys nulled)
  function writeAttributionToCart(choices) {
    var analytics = hasAnalytics(choices);
    var marketing = hasMarketing(choices);

    if (!analytics && !marketing) {
      clearAttributionFromCart();
      return;
    }

    var attributes = {};

    // Analytics lane: first-party order↔session join keys + session context.
    if (analytics) {
      var ids = readDenkIds();
      if (ids) {
        if (ids.clientId) attributes._denk_id = ids.clientId;
        if (ids.sessionId) attributes._denk_session = ids.sessionId;
        if (ids.landingPath) attributes._denk_landing_path = ids.landingPath;
        if (ids.referrer) attributes._denk_referrer = ids.referrer;
      }
      attributes._denk_consent_analytics = 'granted';
      // Inquiry id (Anfragen): lets the order matcher link a later order to the
      // WhatsApp/mail/form inquiry of this session. Only from the SDK's lead
      // API, which returns null without analytics/marketing consent; an old
      // SDK without denk.lead simply writes nothing here.
      var lead = readCurrentLead();
      if (lead && lead.leadId) attributes._denk_lead = lead.leadId;
    }

    // No marketing grant (withdrawn, or never given): cart/update.js MERGES attributes, so
    // the ad flags and click ids of an earlier marketing grant would stay on
    // the cart. Null them explicitly (part of the signature: written once).
    // The click-id stash (localStorage, marketing category) goes with it.
    if (!marketing) {
      MARKETING_KEYS.forEach(function (k) { attributes[k] = null; });
      clearStash();
    }

    // Marketing lane: the ad-consent flags the webhook reads + the ad click ids.
    // denk-CMP's single "marketing" toggle governs all Google ad-signals, so
    // ad_user_data and ad_personalization both follow the marketing grant.
    if (marketing) {
      attributes._denk_consent_ad_user_data = 'granted';
      attributes._denk_consent_ad_personalization = 'granted';
      var clickIds = marketingClickIds();
      ALL_KEYS.forEach(function (k) {
        if (
          (k === 'gclid' || k === 'gbraid' || k === 'wbraid' ||
           k === 'fbclid' || k === 'fbc' || k === 'fbp') &&
          clickIds[k]
        ) {
          attributes[k] = clickIds[k];
        }
      });
    }

    // Write once per session unless the attribute set actually changed (a rotated
    // session id or a new click id changes the signature → re-write).
    var signature = JSON.stringify(attributes);
    try {
      if (sessionStorage.getItem(WRITTEN_KEY) === signature) return;
    } catch (e) {}

    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: true
    })
      .then(function () {
        try { sessionStorage.setItem(WRITTEN_KEY, signature); } catch (e) {}
      })
      .catch(function () {});
  }

  // Consent withdrawn: null everything we may have written so nothing rides into
  // the order, and forget the stash — withdrawal must be as easy as granting.
  function clearAttributionFromCart() {
    var hadWritten = false;
    try { hadWritten = !!sessionStorage.getItem(WRITTEN_KEY); } catch (e) {}
    var stash = readStash();
    var stashHasIds = CLICK_PARAMS.some(function (k) { return !!stash[k]; }) || !!stash.fbclid;
    if (!hadWritten && !stashHasIds) {
      // Nothing written in THIS session. The cart lives for days, though: a
      // grant in an earlier session may have left ids on it that a withdrawal
      // in this session must remove. Check the cart we read anyway; no extra
      // write for a visitor whose cart is clean (the usual decliner).
      withCart(function (cart) {
        if (cartCarriesAttributes(cart, ALL_KEYS)) nullCartAttributes(ALL_KEYS);
      });
      return;
    }

    clearStash();
    try { sessionStorage.removeItem(WRITTEN_KEY); } catch (e) {}
    nullCartAttributes(ALL_KEYS);
  }

  // The cart as read at init (or later): cb(cart|null), never throws.
  function withCart(cb) {
    if (latestCart) { cb(latestCart); return; }
    if (!seededCart) seedCart();
    seededCart.then(function (c) { try { cb(c); } catch (e) {} });
  }
  function cartCarriesAttributes(cart, keys) {
    var a = cart && cart.attributes;
    if (!a || typeof a !== 'object') return false;
    return keys.some(function (k) { return a[k] != null && a[k] !== ''; });
  }
  function nullCartAttributes(keys) {
    var attributes = {};
    keys.forEach(function (k) { attributes[k] = null; });
    // Keep the local view in step, so a second check on this page is a no-op.
    if (latestCart && latestCart.attributes) {
      keys.forEach(function (k) { delete latestCart.attributes[k]; });
    }
    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: true
    }).catch(function () {});
  }

  // --- denk.track wrapper (queues until the SDK is INITIALIZED) ---------------
  var queue = [];
  function denkReady() {
    return !!(window.__denkReady && window.denk && typeof window.denk.track === 'function');
  }
  function denkTrack(name, props) {
    if (denkReady()) window.denk.track(name, props);
    else queue.push([name, props]);
  }
  function flushQueue() {
    if (!denkReady()) return;
    while (queue.length) {
      var e = queue.shift();
      window.denk.track(e[0], e[1]);
    }
  }

  // --- commerce events --------------------------------------------------------
  var latestCart = null;
  function shopCurrency() {
    return (latestCart && latestCart.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'EUR';
  }

  var seededCart = null; // Promise of the first cart.js read (or null before init)
  function seedCart() {
    seededCart = fetch(cartRoot() + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { latestCart = c; return c; })
      .catch(function () { return null; });
  }

  function lineValueCents(it) {
    if (it.final_line_price != null) return it.final_line_price;
    var unit = it.final_price != null ? it.final_price : it.price || 0;
    return unit * (it.quantity || 1);
  }

  function onVariantAdd(event) {
    var d = event.detail || {};
    var items = d.items || [];
    if (d.cart) latestCart = d.cart;

    var valueCents = 0;
    var tracked = [];
    items.forEach(function (it) {
      valueCents += lineValueCents(it);
      var unit = it.final_price != null ? it.final_price : it.price || 0;
      tracked.push({
        product_id: it.product_id,
        variant_id: it.variant_id || it.id,
        quantity: it.quantity,
        price: unit / 100,
        // Catalog facts for the brand-level cart analysis (server allowlists
        // exactly these keys on the essential lane; anything else is dropped).
        item_name: it.product_title || it.title,
        item_brand: it.vendor,
        item_variant: it.variant_title
      });
    });

    denkTrack('add_to_cart', {
      value: valueCents / 100,
      currency: (d.cart && d.cart.currency) || shopCurrency(),
      items: tracked
    });

    // A cart change is the moment a fresh cart may be created — re-sync the
    // attribution so the ids are on the cart BEFORE any checkout starts.
    writeAttributionToCart(null);
  }

  function onCartChange(event) {
    if (event.detail && event.detail.cart) latestCart = event.detail.cart;
  }

  // begin_checkout fires once per page across every cart-page checkout path:
  // the normal name="checkout" submit, the VAT-exemption clone redirect, and the
  // non-exempt requestSubmit (which would otherwise double-fire with the submit
  // listener — the idempotent guard prevents that).
  var beginCheckoutFired = false;
  function fireBeginCheckout() {
    if (beginCheckoutFired) return;
    beginCheckoutFired = true;
    var props = {};
    if (latestCart) {
      props.value = (latestCart.total_price || 0) / 100;
      props.currency = latestCart.currency || shopCurrency();
      props.num_items = latestCart.item_count;
    }
    denkTrack('begin_checkout', props);
  }

  function isCartForm(form) {
    return !!(form && form.matches && form.matches('form[action*="/cart"]'));
  }
  // The mwst-befreiung clone is inserted directly before the hidden real button,
  // which carries class 'mwst-original-checkout'.
  function isMwstCheckoutClone(el) {
    var sib = el && el.nextElementSibling;
    return !!(sib && sib.classList && sib.classList.contains('mwst-original-checkout'));
  }

  function onSubmitCapture(event) {
    var form = event.target;
    if (!isCartForm(form)) return;
    // event.submitter is unsupported on Safari < 15.4 / older engines — fall back
    // to the form's own checkout control.
    var btn = event.submitter || form.querySelector('button[name="checkout"], input[name="checkout"]');
    if (!btn || btn.name !== 'checkout') return;
    fireBeginCheckout();
  }

  function onClickCapture(event) {
    var t = event.target;
    var btn = t && t.closest && t.closest('button, input[type="submit"], input[type="button"]');
    if (!btn) return;
    if (btn.name === 'checkout' || isMwstCheckoutClone(btn)) fireBeginCheckout();
  }

  // --- consent-gated external scripts + Liquid event queue --------------------
  function categoryGranted(cat, c) {
    if (cat === 'analytics') return !!(c && c.analytics);
    return !!(c && c.marketing); // default + 'marketing'
  }
  // Activate <script type="text/plain" data-denk-src data-denk-consent="marketing|analytics">
  // once its category is granted. One-way: an executed script cannot be unloaded.
  function activateGatedScripts(choices) {
    var nodes = document.querySelectorAll('script[type="text/plain"][data-denk-src]');
    Array.prototype.forEach.call(nodes, function (n) {
      if (!categoryGranted(n.getAttribute('data-denk-consent') || 'marketing', choices)) return;
      var s = document.createElement('script');
      s.src = n.getAttribute('data-denk-src');
      if (n.hasAttribute('async')) s.async = true;
      if (n.hasAttribute('defer')) s.defer = true;
      // AdSense u.a. verlangen crossorigin auf dem echten Script-Tag.
      if (n.hasAttribute('crossorigin')) s.crossOrigin = n.getAttribute('crossorigin') || 'anonymous';
      if (n.parentNode) n.parentNode.replaceChild(s, n);
    });
  }
  // Drain the shared Liquid event queue (view_item, generate_lead, …) through
  // denk.track (consent-buffered), then forward later pushes live.
  function drainDenkQueue() {
    var q = window.__denkQueue;
    if (q && q.length) {
      q.splice(0, q.length).forEach(function (item) {
        if (item && item.length) denkTrack(item[0], item[1]);
      });
    }
    window.__denkQueue = {
      push: function (item) { if (item && item.length) denkTrack(item[0], item[1]); return 0; }
    };
  }

  // --- consent + SDK wiring ---------------------------------------------------
  function onConsent(choices) {
    writeAttributionToCart(choices);
    activateGatedScripts(choices);
  }
  var denkReadyHandled = false;
  function onDenkReady() {
    // Einmal-Guard: kann vom Polling UND vom 'denk:ready'-Event erreicht werden.
    if (denkReadyHandled) return;
    denkReadyHandled = true;
    flushQueue();
    drainDenkQueue();
    // Returning visitor whose stored decision may predate the first `denk:consent`.
    try {
      var rec = window.denk.getConsent && window.denk.getConsent();
      if (rec) onConsent(rec.choices);
    } catch (e) {}
  }
  function waitForDenk() {
    if (denkReady()) {
      onDenkReady();
      return;
    }
    // Frueher: hartes Aufgeben nach 200x50ms = 10s. Auf langsamen (mobilen)
    // Verbindungen laedt das SDK oft spaeter — dann blieben __denkQueue
    // (view_item, generate_lead) und der interne Puffer (add_to_cart) fuer
    // immer gestrandet, waehrend spaete Direkt-Tracks durchkamen. Jetzt:
    // 10s Schnellpolling, danach 1s-Takt ohne Aufgeben.
    var n = 0;
    var t = window.setInterval(function () {
      if (denkReady()) {
        window.clearInterval(t);
        onDenkReady();
        return;
      }
      if (++n >= 200) {
        window.clearInterval(t);
        t = window.setInterval(function () {
          if (denkReady()) {
            window.clearInterval(t);
            onDenkReady();
          }
        }, 1000);
      }
    }, 50);
  }

  captureClickIds();
  document.addEventListener('variant:add', onVariantAdd);
  document.addEventListener('cart:change', onCartChange);
  document.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('denk:consent', function (e) { onConsent(e.detail); });
  // A new inquiry (contact click, form, B2B application) → put its id on the
  // cart now, so a checkout started afterwards carries it. Consent-gated inside.
  // window + capture hears the event whether the SDK dispatches it on window or
  // on document; a duplicate call is harmless (signature dedupe).
  window.addEventListener('denk:lead', function () { writeAttributionToCart(null); }, true);
  // Loader signalisiert SDK-Bereitschaft aktiv — unabhaengig vom Polling.
  document.addEventListener('denk:ready', onDenkReady);

  function init() {
    seedCart();
    waitForDenk();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
